/**
 * Time Entry Service — CRUD for annotator hour logging.
 *
 * This is the ONLY place that writes to the `time_entry` table.
 * A database trigger (`sync_assignment_hours`) keeps
 * `assignment.hours_logged` in sync automatically.
 *
 * Flow:
 *   annotator calls logHours() → time_entry row created/updated →
 *   DB trigger fires → assignment.hours_logged recomputed →
 *   all downstream KPIs (Dashboard, My Work, Profile) read
 *   assignment.hours_logged and get the correct number.
 *
 * Auth:
 *   RLS policies enforce that annotators can only CRUD their own entries.
 *   SPLs/admins can read all org entries and approve/reject.
 *   This service still checks getAuthUserId() as a client-side guard.
 */

import { supabase, getAuthUserId } from '../lib/supabase';
import { logAudit, AUDIT_ACTIONS } from './auditService';
import { safeQuery, safeMutation } from '../utils/safeSupabase';

// ─── Helpers ──────────────────────────────────────────────

function getWeekStart(date = new Date()) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay()); // Sunday
  d.setHours(0, 0, 0, 0);
  return d;
}

function toDateString(date) {
  if (typeof date === 'string') return date.slice(0, 10); // Already ISO or YYYY-MM-DD
  // Use local date, not UTC — prevents logging to wrong day for western timezones
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function requireUser(action) {
  const uid = getAuthUserId();
  if (!uid) throw new Error(`${action} requires authentication`);
  return uid;
}

// ─── Write Operations ─────────────────────────────────────

/**
 * Log hours for a project on a specific date.
 * Uses upsert — creates a new entry or updates an existing one
 * for the same person + assignment + date.
 *
 * @param {Object} params
 * @param {string} params.assignmentId — UUID of the assignment
 * @param {string} params.jobId — UUID of the job (denormalized for queries)
 * @param {string} params.orgId — UUID of the org (optional, for RLS)
 * @param {string} [params.date] — YYYY-MM-DD, defaults to today
 * @param {number} params.hours — hours worked (0.25 to 24)
 * @param {number} [params.items] — items annotated that day (integer, optional)
 * @param {string} [params.note] — optional work description
 * @returns {{ data, error }}
 */
/**
 * Phase 17.6 (D2) — auto-flag heuristics. Time entries auto-approve on
 * submission; suspicious entries are flagged for SPL review WITHOUT
 * blocking payment. Add new heuristics here as they emerge.
 *
 * Returns { flagged: boolean, reason: string|null }.
 *
 * Current heuristics:
 *   - high_hours: > 12 hours in a single day. Anything over 12 in one
 *     day is rare for sustained, focused annotation work — worth a look
 *     from the SPL even if approved automatically.
 *
 * Future heuristics (deferred):
 *   - rate_anomaly: total earnings (hours * rate) exceeds 1.5x daily-avg
 *   - cluster_fatigue: 3+ days in a row at >10h
 *   - off_hours: time logged at suspicious time-of-submission (needs
 *     timezone-aware created_at comparison; not in scope today)
 */
function autoFlagTimeEntry({ hours, dateStr }) {
  if (Number(hours) > 12) {
    return { flagged: true, reason: `high_hours: ${hours}h on ${dateStr}` };
  }
  return { flagged: false, reason: null };
}

export async function logHours({ assignmentId, jobId, orgId, date, hours, items, note }) {
  const userId = requireUser('logHours');
  const dateStr = toDateString(date || new Date());

  // Client-side validation
  if (!assignmentId) return { data: null, error: { message: 'Assignment ID required' } };
  if (!hours || hours <= 0) return { data: null, error: { message: 'Hours must be greater than 0' } };
  if (hours > 24) return { data: null, error: { message: 'Cannot log more than 24 hours in one day' } };

  // Prevent logging future dates
  const today = toDateString(new Date());
  if (dateStr > today) return { data: null, error: { message: 'Cannot log hours for future dates' } };

  // Phase 17.6 — auto-flag suspicious entries before insert
  const { flagged, reason } = autoFlagTimeEntry({ hours, dateStr });

  const result = await safeMutation(
    supabase.from('time_entry').upsert(
      {
        individual_id: userId,
        assignment_id: assignmentId,
        job_id: jobId,
        org_id: orgId || null,
        date: dateStr,
        hours,
        items_completed: items != null ? Math.max(0, Math.floor(items)) : null,
        note: note || null,
        status: 'approved', // D2 — auto-approve regardless of flag (flag is observability, not gating)
        flagged_for_review: flagged,
        flag_reason: reason,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'individual_id,assignment_id,date' }
    ).select().single(),
    'timeEntry-logHours'
  );

  // Phase 17.6 — fire time_entry_flagged email to SPL when flag was set
  // on this submission. Fire-and-forget; never blocks logHours.
  if (flagged && result?.data?.id && orgId) {
    try {
      // Fetch project SPL + annotator name for email context
      const [{ data: project }, { data: annotator }] = await Promise.all([
        supabase.from('job').select('title, project_manager_id').eq('id', jobId).maybeSingle(),
        supabase.from('individual').select('name').eq('id', userId).maybeSingle(),
      ]);
      const splId = project?.project_manager_id;
      if (splId) {
        const portalOrigin = (typeof window !== 'undefined' && window.location?.origin) || 'https://juuniai.com';
        const reviewUrl = `${portalOrigin}/admin/flagged-time-entries?entry=${result.data.id}`;
        const { fireAndSendEmail } = await import('./emailService');
        await fireAndSendEmail(orgId, 'time_entry_flagged', {
          recipientId: splId,
          senderId: splId,
          jobId,
          jobTitle: project?.title || '',
          annotatorName: annotator?.name || '',
          entryDate: dateStr,
          flagReason: reason || '',
          hours,
          reviewUrl,
        }, {
          recipientId: splId,
          jobId,
          // Idempotency keyed by entry id — re-flagging the same entry doesn't refire
          idempotencyKey: `time_entry_flagged:${result.data.id}:${splId}`,
        });
      }
    } catch (emailErr) {
      console.warn('logHours flag email trigger failed:', emailErr?.message || emailErr);
    }
  }

  return result;
}

/**
 * Phase 17.6 — SPL resolves a flagged time entry.
 * Sets flag_resolved_at + flag_resolved_by + flag_resolution. Fires
 * time_entry_flag_resolved email to the annotator.
 *
 * @param {string} entryId
 * @param {'approved_anyway'|'payment_revoked'} resolution
 * @param {string} [note] — optional reviewer note (carries to email)
 * @returns {{ data, error }}
 */
export async function resolveTimeEntryFlag(entryId, resolution, note) {
  const userId = requireUser('resolveTimeEntryFlag');
  if (!entryId) return { data: null, error: { message: 'Entry ID required' } };
  if (!['approved_anyway', 'payment_revoked'].includes(resolution)) {
    return { data: null, error: { message: 'Invalid resolution' } };
  }

  // If payment_revoked, also flip status to 'rejected' to undo auto-approval
  const updates = {
    flag_resolved_at: new Date().toISOString(),
    flag_resolved_by: userId,
    flag_resolution: resolution,
    updated_at: new Date().toISOString(),
  };
  if (resolution === 'payment_revoked') {
    updates.status = 'rejected';
  }

  const result = await safeMutation(
    supabase.from('time_entry').update(updates).eq('id', entryId).select().single(),
    'timeEntry-resolveFlag'
  );
  if (!result?.data) return result;

  // Phase 17.6 — audit-trail completeness (Invariant #21). SPL action on a
  // flagged entry is high-stakes (can revoke payment) so the audit log
  // surfaces it as a distinct action.
  logAudit(AUDIT_ACTIONS.RESOLVE_TIME_ENTRY_FLAG, {
    entityType: 'time_entry',
    entityId: entryId,
    details: {
      resolution,
      hasNote: !!note,
      reverted_status: resolution === 'payment_revoked',
    },
  });

  // Fire time_entry_flag_resolved email to the annotator. Fire-and-forget.
  try {
    const { data: entryRow } = result;
    const { data: annotator } = await supabase
      .from('individual')
      .select('name, email')
      .eq('id', entryRow.individual_id)
      .maybeSingle();
    const { data: project } = await supabase
      .from('job')
      .select('title')
      .eq('id', entryRow.job_id)
      .maybeSingle();
    const resolutionLabel = resolution === 'approved_anyway'
      ? 'Approved — payment proceeds as scheduled.'
      : 'Payment revoked — your project lead has reversed the auto-approval. Reach out to clarify.';
    const { fireAndSendEmail } = await import('./emailService');
    await fireAndSendEmail(entryRow.org_id, 'time_entry_flag_resolved', {
      recipientId: entryRow.individual_id,
      recipientName: annotator?.name || '',
      email: annotator?.email || '',
      jobId: entryRow.job_id,
      jobTitle: project?.title || '',
      entryDate: entryRow.date,
      resolutionLabel,
      resolutionNote: note || '',
    }, {
      recipientId: entryRow.individual_id,
      jobId: entryRow.job_id,
      idempotencyKey: `time_entry_flag_resolved:${entryId}:${entryRow.individual_id}`,
    });
  } catch (emailErr) {
    console.warn('resolveTimeEntryFlag email trigger failed:', emailErr?.message || emailErr);
  }

  return result;
}

/**
 * Delete a time entry for a specific assignment and date.
 * Only works for the current user's own entries.
 *
 * @param {string} assignmentId
 * @param {string} date — YYYY-MM-DD
 * @returns {{ data, error }}
 */
export async function deleteEntry(assignmentId, date) {
  const userId = requireUser('deleteEntry');

  return safeMutation(
    supabase.from('time_entry')
      .delete()
      .eq('individual_id', userId)
      .eq('assignment_id', assignmentId)
      .eq('date', toDateString(date)),
    'timeEntry-delete'
  );
}

// ─── Read Operations (Annotator) ──────────────────────────

/**
 * Fetch today's time entries for the current user across all projects.
 * Used by Dashboard "Log Hours" section.
 *
 * @returns {Array<{ id, assignment_id, job_id, date, hours, note, status }>}
 */
export async function fetchTodayEntries() {
  const userId = getAuthUserId();
  if (!userId) return [];

  const { data } = await safeQuery(
    supabase.from('time_entry')
      .select('id, assignment_id, job_id, date, hours, items_completed, note, status')
      .eq('individual_id', userId)
      .eq('date', toDateString(new Date()))
      .order('created_at', { ascending: false }),
    'timeEntry-fetchToday'
  );

  return data || [];
}

/**
 * Fetch the current week's time entries for the current user.
 * Returns entries grouped by date for weekly summary display.
 *
 * @param {Date} [weekStart] — start of week (defaults to current week's Sunday)
 * @returns {Array<{ id, assignment_id, job_id, date, hours, note, status }>}
 */
export async function fetchWeekEntries(weekStart) {
  const userId = getAuthUserId();
  if (!userId) return [];

  const start = weekStart || getWeekStart();
  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  const { data } = await safeQuery(
    supabase.from('time_entry')
      .select('id, assignment_id, job_id, date, hours, items_completed, note, status')
      .eq('individual_id', userId)
      .gte('date', toDateString(start))
      .lte('date', toDateString(end))
      .order('date', { ascending: true }),
    'timeEntry-fetchWeek'
  );

  return data || [];
}

/**
 * Fetch time entries for a specific assignment, optionally filtered by date range.
 * Used by JobDetail time log history and My Work weekly summary.
 *
 * @param {string} assignmentId
 * @param {Object} [options]
 * @param {string} [options.startDate] — YYYY-MM-DD
 * @param {string} [options.endDate] — YYYY-MM-DD
 * @param {number} [options.limit] — max entries to return (default 60)
 * @returns {Array}
 */
export async function fetchEntriesForAssignment(assignmentId, { startDate, endDate, limit = 60 } = {}) {
  const userId = getAuthUserId();
  if (!userId || !assignmentId) return [];

  let query = supabase.from('time_entry')
    .select('id, assignment_id, job_id, date, hours, items_completed, note, status, created_at')
    .eq('assignment_id', assignmentId)
    .eq('individual_id', userId)
    .order('date', { ascending: false })
    .limit(limit);

  if (startDate) query = query.gte('date', toDateString(startDate));
  if (endDate) query = query.lte('date', toDateString(endDate));

  const { data } = await safeQuery(query, 'timeEntry-fetchForAssignment');
  return data || [];
}

/**
 * Fetch all time entries for the current user within a date range.
 * Used by Profile and reporting.
 *
 * @param {Object} [options]
 * @param {string} [options.startDate]
 * @param {string} [options.endDate]
 * @param {number} [options.limit]
 * @returns {Array}
 */
export async function fetchEntriesForUser({ startDate, endDate, limit = 200 } = {}) {
  const userId = getAuthUserId();
  if (!userId) return [];

  let query = supabase.from('time_entry')
    .select('id, assignment_id, job_id, date, hours, items_completed, note, status, created_at')
    .eq('individual_id', userId)
    .order('date', { ascending: false })
    .limit(limit);

  if (startDate) query = query.gte('date', toDateString(startDate));
  if (endDate) query = query.lte('date', toDateString(endDate));

  const { data } = await safeQuery(query, 'timeEntry-fetchForUser');
  return data || [];
}

// ─── Read Operations (SPL / Admin) ────────────────────────

/**
 * Fetch all pending time entries for the org (SPL approval queue).
 *
 * @returns {Array<{ id, individual_id, assignment_id, job_id, date, hours, note, status,
 *                    individual: { name, email }, job: { title } }>}
 */
export async function fetchPendingForOrg() {
  const { data } = await safeQuery(
    supabase.from('time_entry')
      .select('*, individual:individual_id(name, email), job:job_id(title)')
      .eq('status', 'pending')
      .order('date', { ascending: false })
      .limit(100),
    'timeEntry-fetchPending'
  );

  return data || [];
}

/**
 * Fetch recent time entries for specific jobs (SPL-scoped review).
 * Returns entries from the last 14 days with annotator + job details.
 *
 * @param {string[]} jobIds — UUIDs of jobs to fetch entries for
 * @param {number} [days=14] — lookback window
 * @returns {Array<{ id, individual_id, assignment_id, job_id, date, hours, note, status,
 *                    individual: { name, email }, job: { title, domain } }>}
 */
export async function fetchRecentForJobs(jobIds, days = 14) {
  if (!jobIds?.length) return [];

  const since = new Date();
  since.setDate(since.getDate() - days);

  const { data } = await safeQuery(
    supabase.from('time_entry')
      .select('*, individual:individual_id(name, email), job:job_id(title, domain)')
      .in('job_id', jobIds)
      .gte('date', toDateString(since))
      .order('date', { ascending: false })
      .limit(200),
    'timeEntry-fetchRecentForJobs'
  );

  return data || [];
}

/**
 * Track D dive #46 (CRITIQUE fix, 2026-05-04): server-side filter
 * for pending entries.
 *
 * Pre-fix, TimeApproval.jsx pulled fetchRecentForJobs(jobIds, 30)
 * (up to 200 rows of all statuses across 30 days) then client-side
 * filtered .filter(e => e.status === 'pending'). For an SPL with
 * 100s of approved entries per month, this is unnecessary network +
 * memory + parse cost. Server-side filter pushes the work to
 * Postgres + reduces transferred rows by 95%+ in healthy state.
 *
 * @param {string[]} jobIds — UUIDs of jobs to scope the query
 * @param {number} [days=30] — lookback window
 * @returns {Array<time_entry rows where status='pending'>}
 */
export async function fetchPendingForJobs(jobIds, days = 30) {
  if (!jobIds?.length) return [];

  const since = new Date();
  since.setDate(since.getDate() - days);

  const { data } = await safeQuery(
    supabase.from('time_entry')
      .select('*, individual:individual_id(name, email), job:job_id(title, domain)')
      .in('job_id', jobIds)
      .eq('status', 'pending')
      .gte('date', toDateString(since))
      .order('date', { ascending: false })
      .limit(500),  // higher cap since we already filtered to 'pending'
    'timeEntry-fetchPendingForJobs'
  );

  return data || [];
}

/**
 * Fetch hours grouped by assignment_id for a date range.
 * Returns a Map<assignmentId, { total, approved, pending }>.
 * Used by dashboards to get time-windowed hours instead of lifetime hours_logged.
 *
 * @param {string} orgId
 * @param {Object} [dateRange] - { start: Date, end: Date } or null for all time
 * @returns {Map<string, { total: number, approved: number, pending: number }>}
 */
export async function fetchHoursByAssignmentForRange(orgId, dateRange) {
  if (!orgId) return new Map();

  let query = supabase.from('time_entry')
    .select('assignment_id, hours, status')
    .eq('org_id', orgId);

  if (dateRange?.start) query = query.gte('date', toDateString(dateRange.start));
  if (dateRange?.end) query = query.lte('date', toDateString(dateRange.end));
  query = query.limit(5000); // Safety bound — typical org won't exceed this in a month

  const { data } = await safeQuery(query, 'timeEntry-fetchHoursByRange');

  const result = new Map();
  for (const entry of (data || [])) {
    const key = entry.assignment_id;
    if (!result.has(key)) result.set(key, { total: 0, approved: 0, pending: 0 });
    const h = parseFloat(entry.hours || 0);
    const bucket = result.get(key);
    bucket.total += h;
    if (entry.status === 'approved') bucket.approved += h;
    if (entry.status === 'pending') bucket.pending += h;
  }

  return result;
}

/**
 * Fetch time entries for a specific person (admin/SPL use — not limited to current user).
 * Joins job title for chart labels.
 *
 * @param {string} individualId — UUID of the annotator
 * @param {Object} [options]
 * @param {string} [options.startDate] — YYYY-MM-DD
 * @param {string} [options.endDate] — YYYY-MM-DD
 * @param {string} [options.jobId] — filter to a specific job
 * @param {number} [options.limit] — max entries (default 500)
 * @returns {Array<{ date, hours, status, job_id, job: { title, domain } }>}
 */
export async function fetchEntriesForPerson(individualId, { startDate, endDate, jobId, limit = 500 } = {}) {
  if (!individualId) return [];

  let query = supabase.from('time_entry')
    .select('date, hours, items_completed, status, job_id, assignment_id, job:job_id(title, domain)')
    .eq('individual_id', individualId)
    .order('date', { ascending: true })
    .limit(limit);

  if (startDate) query = query.gte('date', toDateString(startDate));
  if (endDate) query = query.lte('date', toDateString(endDate));
  if (jobId) query = query.eq('job_id', jobId);

  const { data } = await safeQuery(query, 'timeEntry-fetchForPerson');
  return data || [];
}

// ─── Approval Operations (SPL / Admin) ────────────────────

/**
 * Approve a time entry. Changes status to 'approved', which
 * triggers the DB function to update assignment.hours_logged.
 *
 * @param {string} entryId — UUID of the time_entry
 * @returns {{ data, error }}
 */
export async function approveEntry(entryId) {
  const approverId = requireUser('approveEntry');

  const result = await safeMutation(
    supabase.from('time_entry')
      .update({
        status: 'approved',
        approved_by: approverId,
        approved_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', entryId)
      .select()
      .single(),
    'timeEntry-approve'
  );
  if (!result.error && result.data) {
    // Fire-and-forget audit. Safe to reference AUDIT_ACTIONS here; auditService
    // is already imported for other call sites and has zero side effects at import.
    logAudit(AUDIT_ACTIONS.APPROVE_TIME_ENTRY, {
      entityType: 'time_entry',
      entityId: entryId,
      details: { hours: result.data.hours, date: result.data.date, job_id: result.data.job_id },
    });
  }
  return result;
}

/**
 * Reject a time entry. Sets status to 'rejected'.
 * The DB trigger will exclude rejected entries from the hours sum.
 *
 * @param {string} entryId — UUID of the time_entry
 * @param {string} [reason] — optional rejection reason
 * @returns {{ data, error }}
 */
export async function rejectEntry(entryId, reason) {
  const approverId = requireUser('rejectEntry');

  const result = await safeMutation(
    supabase.from('time_entry')
      .update({
        status: 'rejected',
        approved_by: approverId,
        approved_at: new Date().toISOString(),
        note: reason ? `[Rejected] ${reason}` : undefined,
        updated_at: new Date().toISOString(),
      })
      .eq('id', entryId)
      .select()
      .single(),
    'timeEntry-reject'
  );
  if (!result.error && result.data) {
    logAudit(AUDIT_ACTIONS.REJECT_TIME_ENTRY, {
      entityType: 'time_entry',
      entityId: entryId,
      details: { hours: result.data.hours, date: result.data.date, job_id: result.data.job_id, reason: reason || null },
    });
  }
  return result;
}

/**
 * Bulk approve all pending entries for an org.
 * Used by SPLs at end of day/week.
 *
 * @param {string[]} entryIds — array of time_entry UUIDs
 * @returns {{ data, error }}
 */
export async function bulkApprove(entryIds) {
  if (!entryIds?.length) return { data: [], error: null };
  const approverId = requireUser('bulkApprove');

  const result = await safeMutation(
    supabase.from('time_entry')
      .update({
        status: 'approved',
        approved_by: approverId,
        approved_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .in('id', entryIds)
      .select(),
    'timeEntry-bulkApprove'
  );

  // Phase 11 slot 5: bulk approval — admin payroll-precursor op. The
  // per-entry APPROVE_TIME_ENTRY audit doesn't fire from the bulk path.
  if (!result.error) {
    logAudit(AUDIT_ACTIONS.BULK_APPROVE_TIME_ENTRIES, {
      entityType: 'time_entry',
      entityId: null,
      details: {
        count: entryIds.length,
        // Trim to first 50 ids for forensic detail without bloating JSONB.
        entryIds: entryIds.slice(0, 50),
        trimmed: entryIds.length > 50,
        approverId,
      },
    });
  }

  return result;
}

// ─── Aggregation Helpers ──────────────────────────────────

/**
 * Summarize hours for the current user, grouped by assignment.
 * Useful for Dashboard/Profile to show per-project hours
 * without reading the (potentially stale) assignment.hours_logged.
 *
 * @param {Object} [options]
 * @param {string} [options.startDate]
 * @param {string} [options.endDate]
 * @returns {Map<assignmentId, { total, approved, pending }>}
 */
export async function getHoursSummaryByAssignment({ startDate, endDate } = {}) {
  const entries = await fetchEntriesForUser({ startDate, endDate, limit: 1000 });

  const summary = new Map();
  for (const entry of entries) {
    const key = entry.assignment_id;
    if (!summary.has(key)) summary.set(key, { total: 0, approved: 0, pending: 0 });
    const s = summary.get(key);
    s.total += parseFloat(entry.hours);
    if (entry.status === 'approved') s.approved += parseFloat(entry.hours);
    if (entry.status === 'pending') s.pending += parseFloat(entry.hours);
  }

  return summary;
}

/**
 * Get total hours for a specific day across all projects.
 * Used to warn if the annotator is exceeding reasonable daily hours.
 *
 * @param {string} [date] — YYYY-MM-DD, defaults to today
 * @returns {number} total hours logged for that day
 */
export async function getDayTotal(date) {
  const userId = getAuthUserId();
  if (!userId) return 0;

  const { data } = await safeQuery(
    supabase.from('time_entry')
      .select('hours')
      .eq('individual_id', userId)
      .eq('date', toDateString(date || new Date())),
    'timeEntry-dayTotal'
  );

  return (data || []).reduce((sum, e) => sum + parseFloat(e.hours), 0);
}
